import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CheckBox,
  FormField,
  Heading,
  Layer,
  Select,
  Text,
} from "grommet";
import {
  Add,
  Refresh,
  StatusCritical,
  StatusGood,
  StatusWarning,
  Trash,
} from "grommet-icons";
import { useTranslation } from "react-i18next";
import { CommerceService } from "../../services";
import { presentAlertPrompt } from "@shared/Alert";
import { dismissLoading, presentLoading } from "@shared/Loading";
import { presentToastErrorContent, presentToastSuccess } from "@shared/Toast";
import "./SettingsIntegrations.scss";

export default function SettingsIntegrations() {
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [providerSlug, setProviderSlug] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const refresh = async () => {
    try {
      const [providerResponse, connectionResponse] = await Promise.all([
        CommerceService.getProviders(),
        CommerceService.getConnections(),
      ]);
      setProviders(providerResponse.data);
      setConnections(connectionResponse.data);
    } catch (error) {
      presentToastErrorContent(error);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const openDialog = () => {
    setProviderSlug("");
    setServiceId("");
    setServiceOptions([]);
    setDialogOpen(true);
  };

  const selectProvider = async (nextProviderSlug) => {
    setProviderSlug(nextProviderSlug);
    setServiceId("");
    setServiceOptions([]);

    const provider = providers.find((item) => item.slug === nextProviderSlug);
    if (!provider?.available) return;

    setLoadingOptions(true);
    try {
      const response =
        await CommerceService.getConnectionOptions(nextProviderSlug);
      setServiceOptions(response.data);
    } catch (error) {
      presentToastErrorContent(error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const connect = async () => {
    presentLoading();
    try {
      await CommerceService.createConnection({
        provider: providerSlug,
        externalServiceId: serviceId,
      });
      setDialogOpen(false);
      await refresh();
      presentToastSuccess(t("settingsIntegrations.connected"));
    } catch (error) {
      presentToastErrorContent(error);
    } finally {
      dismissLoading();
    }
  };

  const sync = async (connectionId) => {
    presentLoading();
    try {
      await CommerceService.syncConnection(connectionId);
      await refresh();
      presentToastSuccess(t("settingsIntegrations.synced"));
    } catch (error) {
      presentToastErrorContent(error);
    } finally {
      dismissLoading();
    }
  };

  const setEnabled = async (connectionId, enabled) => {
    presentLoading();
    try {
      await CommerceService.setConnectionEnabled(connectionId, enabled);
      await refresh();
      presentToastSuccess(
        t(
          enabled
            ? "settingsIntegrations.enabled"
            : "settingsIntegrations.disabled",
        ),
      );
    } catch (error) {
      await refresh();
      presentToastErrorContent(error);
    } finally {
      dismissLoading();
    }
  };

  const setActivationMode = async (connectionId, activationMode) => {
    presentLoading();
    try {
      await CommerceService.setActivationMode(connectionId, activationMode);
      await refresh();
      presentToastSuccess(t("settingsIntegrations.activationModeUpdated"));
    } catch (error) {
      presentToastErrorContent(error);
    } finally {
      dismissLoading();
    }
  };

  const remove = (connection) => {
    const providerName =
      providers.find((provider) => provider.slug === connection.provider)
        ?.name || connection.provider;
    presentAlertPrompt({
      title: t("settingsIntegrations.removeTitle"),
      message: t("settingsIntegrations.removeMessage", {
        name: connection.externalServiceName,
        provider: providerName,
      }),
      buttonOKText: t("settingsIntegrations.remove"),
      buttonOKColor: "status-critical",
      onOK: async () => {
        try {
          await CommerceService.deleteConnection(connection._id);
          await refresh();
          presentToastSuccess(t("settingsIntegrations.removed"));
        } catch (error) {
          presentToastErrorContent(error);
        }
      },
    });
  };

  const selectedProvider = providers.find(
    (provider) => provider.slug === providerSlug,
  );

  return (
    <div className="settings-integrations">
      <Box direction="row" justify="between" align="center" gap="medium">
        <Box>
          <Heading level="1" margin={{ bottom: "xsmall" }}>
            {t("settingsIntegrations.title")}
          </Heading>
          <Text color="dark-5">{t("settingsIntegrations.description")}</Text>
        </Box>
        <Button
          primary
          icon={<Add size="small" />}
          label={t("settingsIntegrations.add")}
          onClick={openDialog}
        />
      </Box>

      <Box margin={{ top: "large" }} gap="small">
        {connections.length === 0 && (
          <Box border pad="large" align="center">
            <Text color="dark-5">{t("settingsIntegrations.empty")}</Text>
          </Box>
        )}
        {connections.map((connection) => {
          const healthy = connection.lastSyncStatus !== "error";
          const enabled = connection.enabled !== false;
          const providerName =
            providers.find((provider) => provider.slug === connection.provider)
              ?.name || connection.provider;
          const activationModeOptions = ["integration", "loxe"].map(
            (value) => ({
              value,
              label: t(`settingsIntegrations.activationMode.${value}`),
            }),
          );
          return (
            <Box
              key={connection._id}
              className="integration-row"
              border
              pad="medium"
              direction="row"
              justify="between"
              align="center"
              gap="medium"
            >
              <Box direction="row" align="center" gap="medium">
                {!healthy ? (
                  <StatusCritical color="status-critical" size="medium" />
                ) : enabled ? (
                  <StatusGood color="status-ok" size="medium" />
                ) : (
                  <StatusWarning
                    color="status-warning"
                    size="medium"
                    a11yTitle={t("settingsIntegrations.disabled")}
                  />
                )}
                <Box gap="xxsmall">
                  {enabled ? (
                    <Button
                      className="integration-title-link"
                      plain
                      href={`/settings/upsell?connection=${connection._id}`}
                      label={connection.externalServiceName}
                    />
                  ) : (
                    <Text weight="bold">{connection.externalServiceName}</Text>
                  )}
                  <Text size="small" color="dark-5">
                    {providerName}
                    {connection.externalServiceType
                      ? ` · ${connection.externalServiceType}`
                      : ""}
                    {` · ${t("settingsIntegrations.productCount", {
                      count: connection.productCount || 0,
                    })}`}
                  </Text>
                  <Text size="xsmall" color="dark-5">
                    {connection.lastSyncedAt
                      ? t("settingsIntegrations.lastSynced", {
                          date: new Date(
                            connection.lastSyncedAt,
                          ).toLocaleString(),
                        })
                      : t("settingsIntegrations.notSynced")}
                  </Text>
                  {connection.lastSyncError && (
                    <Text size="small" color="status-critical">
                      {connection.lastSyncError}
                    </Text>
                  )}
                </Box>
              </Box>
              <Box
                className="integration-actions"
                direction="row"
                gap="medium"
                align="center"
              >
                <Box gap="xsmall" className="activation-mode-control">
                  <Text size="xsmall" color="dark-5">
                    {t("settingsIntegrations.manageActivation")}
                  </Text>
                  <Select
                    className="activation-mode-select"
                    value={connection.activationMode || "integration"}
                    valueKey={{ key: "value", reduce: true }}
                    labelKey="label"
                    options={activationModeOptions}
                    onChange={({ value }) =>
                      setActivationMode(connection._id, value)
                    }
                  />
                </Box>
                <CheckBox
                  toggle
                  checked={connection.enabled !== false}
                  onChange={({ target: { checked } }) =>
                    setEnabled(connection._id, checked)
                  }
                  a11yTitle={t("settingsIntegrations.toggle", {
                    name: connection.externalServiceName,
                  })}
                />
                <Button
                  icon={<Refresh size="small" />}
                  label={t("settingsIntegrations.sync")}
                  onClick={() => sync(connection._id)}
                />
                <Button
                  icon={<Trash size="small" />}
                  size="small"
                  primary
                  color="status-critical"
                  a11yTitle={t("settingsIntegrations.remove")}
                  onClick={() => remove(connection)}
                />
              </Box>
            </Box>
          );
        })}
      </Box>

      {dialogOpen && (
        <Layer
          position="center"
          onClickOutside={() => setDialogOpen(false)}
          onEsc={() => setDialogOpen(false)}
        >
          <Box width="medium" pad="large" gap="medium">
            <Heading level="3" margin="none">
              {t("settingsIntegrations.addTitle")}
            </Heading>
            <FormField label={t("settingsIntegrations.provider")}>
              <Select
                placeholder={t("common.select")}
                value={providerSlug}
                valueKey={{ key: "slug", reduce: true }}
                labelKey="name"
                options={providers}
                onChange={({ value }) => selectProvider(value)}
              />
            </FormField>
            {selectedProvider && !selectedProvider.available && (
              <Text color="status-critical" size="small">
                {selectedProvider.unavailableReason}
              </Text>
            )}
            {providerSlug && selectedProvider?.available && (
              <FormField label={t("settingsIntegrations.service")}>
                <Select
                  placeholder={
                    loadingOptions
                      ? t("settingsIntegrations.loadingServices")
                      : t("common.select")
                  }
                  value={serviceId}
                  valueKey={{ key: "id", reduce: true }}
                  labelKey="name"
                  options={serviceOptions}
                  disabled={loadingOptions}
                  onChange={({ value }) => setServiceId(value)}
                />
              </FormField>
            )}
            <Box direction="row" justify="end" gap="small">
              <Button
                label={t("common.cancel")}
                onClick={() => setDialogOpen(false)}
              />
              <Button
                primary
                label={t("settingsIntegrations.connect")}
                disabled={!providerSlug || !serviceId}
                onClick={connect}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </div>
  );
}
